import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';

import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import type { IndeedSendAttempt } from 'clients/CampaignsClient/CampaignsClient.types';
import { logger } from 'config/logger';
import { queryClient } from 'config/queryClient';
import { QueryKey } from 'types/query';
import { useIndeedConfig } from './useIndeedConfig';
import { sendIndeedMessageRaw } from './useIndeedSendMessageMutation';

const POLL_BASE_MS = 2 * 60 * 1000;
const POLL_JITTER_MS = 15 * 1000;
const randomPollIntervalMs = () => POLL_BASE_MS + Math.round((Math.random() * 2 - 1) * POLL_JITTER_MS);

export const useIndeedSendAttemptPoller = () => {
  const indeedConfig = useIndeedConfig();
  const isProcessingRef = useRef(false);
  const pollIntervalMsRef = useRef(randomPollIntervalMs());

  const canPoll = indeedConfig.isLoggedIn && !indeedConfig.isInitialLoading;

  const { data: pendingAttempts } = useQuery(
    [QueryKey.useIndeedSendAttemptsPendingQuery],
    async () => (await campaignsClient.getPendingIndeedSendAttempts()).data,
    {
      enabled: canPoll,
      refetchInterval: pollIntervalMsRef.current,
      refetchIntervalInBackground: true,
    },
  );

  useEffect(() => {
    if (!canPoll || !pendingAttempts?.length || isProcessingRef.current) return;

    isProcessingRef.current = true;

    const run = async () => {
      for (const attempt of pendingAttempts) {
        await tryAttempt(attempt, indeedConfig.headers);
      }
    };

    run()
      .catch((error) => logger.error(error, { context: 'IndeedSendAttemptPoller' }))
      .finally(() => {
        isProcessingRef.current = false;
        queryClient.invalidateQueries([QueryKey.useIndeedSendAttemptsPendingQuery]);
        queryClient.invalidateQueries([QueryKey.useIndeedCommonModelQuery], { exact: false });

        queryClient.invalidateQueries([QueryKey.useCandidateHistoryQuery], { exact: false });
        queryClient.invalidateQueries([QueryKey.useCandidateActivityLogQuery], { exact: false });
      });
  }, [pendingAttempts, canPoll]);
};

const tryAttempt = async (attempt: IndeedSendAttempt, headers: Record<string, any>) => {
  try {
    const { status, body } = await sendIndeedMessageRaw(
      {
        candidateId: attempt.es_person_id,
        message: attempt.payload.message,
        senderName: attempt.payload.sender_name,
        senderCompany: attempt.payload.sender_company,
        jobTitle: attempt.payload.job_title,
        projectKey: attempt.payload.project_key,
        job: attempt.payload.job as any,
      },
      headers,
    );

    const success = status === 200 && isSuccessfulSendBody(body);

    await campaignsClient.reportIndeedSendAttemptResult({
      attempt_id: attempt.id,
      success,
      response_status: status,
      response_body: body,
      request_headers: headers,
    });
  } catch (error) {
    logger.error(error, { context: 'IndeedSendAttemptPollerTry', attemptId: attempt.id });
  }
};

const isSuccessfulSendBody = (body: string | null): boolean => {
  if (!body) return false;

  try {
    const parsed = JSON.parse(body);
    const results = parsed?.data?.createTalentEngagements?.results ?? [];

    return results.length > 0 && results.every((r: any) => r.__typename !== 'CreateTalentEngagementError');
  } catch {
    return false;
  }
};
